import { DialogContent } from '@mui/material'

import { ConfigurableProperty, DigitalTwin, DigitalTwinSettingTarget } from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingLevel, DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'ui/components/Dialog/Dialog'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import { DigitalTwinSettingDialog } from '../DigitalTwinSettingDialogContent/DigitalTwinSettingDialog'
import { CreateSettingData } from '../DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import { CreateSettingButton } from '../DigitalTwinSettingEditor/CreateSettingButton'
import { getNewPropertySetting, toEditableSetting } from '../DigitalTwinSettingEditor/editorSetting.helper'
import {
  getEditorTitle,
  getPropertyHelpText,
  getPropertyTitle,
  getSubmitLabel,
} from '../DigitalTwinSettingEditor/editorTitle.helper'
import { SettingEditorBanners } from '../DigitalTwinSettingEditor/SettingEditorBanners'
import { getSettingEditorState } from '../DigitalTwinSettingEditor/settingEditorState.helper'
import { useConfirmedClose } from '../DigitalTwinSettingEditor/useConfirmedClose'
import { usePropertySettings } from '../DigitalTwinSettingEditor/usePropertySettings'
import { useTimeSeriesSettingAccess } from '../DigitalTwinSettingForm/useTimeSeriesSettingAccess'
import styles from './DigitalTwinEditSettingsModal.module.less'
import PreviousSettingsTable from './PreviousSettingsTable/PreviousSettingsTable'

type DigitalTwinEditSettingsModalProps = {
  onClose: () => void
  digitalTwin: DigitalTwin
  level: string | undefined
  attribute?: string
  name: string
  permissions: SettingsPermissions
  priorities: SystemSettingPriorityLevel[]
  baseSettingPriorities?: SystemSettingPriorityLevel[]
  originalPriority?: number
  onSettingModified: () => void
  isBaseSettings?: boolean
  isCollectedSettings?: boolean
  period?: Period | undefined
  configurableProperties: ConfigurableProperty[]
}

export default function DigitalTwinEditSettingsModal({
  onClose,
  configurableProperties,
  digitalTwin,
  level,
  attribute,
  name,
  period,
  permissions,
  priorities,
  baseSettingPriorities,
  onSettingModified,
  isBaseSettings = false,
  isCollectedSettings = false,
}: DigitalTwinEditSettingsModalProps): ReactElement {
  const [settingToCreate, setSettingToCreate] = useState<CreateSettingData | null>(null)
  const [copy, setCopy] = useState<CreateSettingData | null>(null)

  const { t } = useTranslation()
  const popupClose = useConfirmedClose(onClose)
  const formClose = useConfirmedClose(() => setSettingToCreate(null))
  const [reportUnsavedChanges] = useState(() => (hasUnsavedChanges: boolean): void => {
    popupClose.onUnsavedChangesChange(hasUnsavedChanges)
    formClose.onUnsavedChangesChange(hasUnsavedChanges)
  })

  const property: DigitalTwinSettingTarget = { level: level as DeviationSettingLevel, name, attribute: attribute ?? '' }
  const [settingsRevision, setSettingsRevision] = useState(0)
  const { settings, operationalEvents, refetchOperationalEvents } = usePropertySettings({
    digitalTwin,
    configurableProperties,
    property,
    priorities,
    refetchKey: settingsRevision,
  })

  const doOnSettingModified = (): void => {
    setSettingsRevision((revision) => revision + 1)
    onSettingModified()
  }

  const hasAccessToTimeseries = useTimeSeriesSettingAccess()

  const onCreateSettingClick = (isTimeSeries: boolean): void => {
    setSettingToCreate(
      getNewPropertySetting({
        property,
        isTimeSeries,
        isBaseSettings,
        priorities,
        configurableProperties,
        isCollectedSettings,
        period,
      })
    )
  }

  const editorState = settingToCreate && getSettingEditorState({ setting: settingToCreate, copy, isCollectedSettings })

  const submitLabel = getSubmitLabel(
    { isTimeSeries: !!editorState?.isTimeSeries, mode: editorState?.mode ?? 'create' },
    t
  )

  const helpText = getPropertyHelpText(property, digitalTwin)
  const title = getEditorTitle({
    propertyTitle: getPropertyTitle(property, digitalTwin),
    editedSetting: editorState && { isTimeSeries: editorState.isTimeSeries, mode: editorState.mode },
    t,
  })

  const onEditSettings = (setting: DigitalTwinSetting): void => {
    setSettingToCreate(toEditableSetting(setting))
  }

  return (
    <Dialog
      open={attribute !== undefined}
      onClose={popupClose.requestClose}
      aria-labelledby="form-dialog-title"
      maxWidth="xl"
      fullWidth
      data-testid="deviation-setting-dialog" // e2e: settings dialog selector — do not remove
    >
      <CustomDialogTitle
        title={
          <div>
            <b>{title}</b>
            <i className={styles.DigitalTwinEditSettingsModal_Title_Helptext}>{helpText}</i>
          </div>
        }
        handleClose={popupClose.requestClose}
      />
      <DialogContent>
        {!settingToCreate && (
          <div className={styles.DigitalTwinEditSettingsModal_Button__right}>
            <CreateSettingButton
              allowTimeSeries={hasAccessToTimeseries && !isBaseSettings}
              disabled={!permissions.canEdit}
              onCreate={onCreateSettingClick}
            />
          </div>
        )}
        <SettingEditorBanners
          lacksTimeSeriesAccess={!!editorState?.isTimeSeries && !hasAccessToTimeseries}
          isEventSetting={!!editorState?.isEventSetting}
          mode={editorState?.mode ?? 'create'}
          copyActionLabel={submitLabel}
          isBaseSettingInCollectedSettings={!!editorState?.isBaseSettingInCollectedSettings}
        />
        <DigitalTwinSettingDialog
          initData={settingToCreate}
          onSettingModified={doOnSettingModified}
          onClose={() => setSettingToCreate(null)}
          configurableProperties={configurableProperties}
          propertyItems={[]}
          digitalTwin={digitalTwin}
          allowEmpty={false}
          priorities={priorities}
          baseSettingPriorities={baseSettingPriorities}
          isBaseSettings={isBaseSettings}
          isCollectedSettings={isCollectedSettings}
          hasUnitAndProperty={true}
          submitLabel={submitLabel}
          hideActions={editorState?.isTimeSeries && !hasAccessToTimeseries}
          disable={!!editorState?.isLocked}
          onCopyChange={setCopy}
          onUnsavedChangesChange={reportUnsavedChanges}
          onRequestClose={formClose.requestClose}
        />

        <PreviousSettingsTable
          allSettingsForChosenProperty={settings}
          operationalEvents={operationalEvents}
          onEventPostModalClose={refetchOperationalEvents}
          onEditSetting={onEditSettings}
          permissions={permissions}
          isBaseSettings={isBaseSettings}
          priorities={priorities}
        />
      </DialogContent>
      {popupClose.closeConfirmDialog}
      {formClose.closeConfirmDialog}
    </Dialog>
  )
}
